import React, { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { PRODUCTS, buildCart } from "@/lib/configurator";
import { Package, ShieldCheck, Sparkles, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";

const EDITION_KEYS = { express: "Express+", professional: "Professional+", expert: "Expert", corporate: "Corporate" };

export default function LicenseConfigurator({ edition, defaultDeviceCount, value, onChange }) {
  const val = value || { deviceCount: defaultDeviceCount || 1, carePackage: "carePlus", careDuration: "1" };
  const [local, setLocal] = useState(val);
  const state = value || local;

  const setState = (patch) => {
    const next = { ...state, ...patch };
    setLocal(next);
    if (onChange) onChange(next);
  };

  const cart = useMemo(
    () => buildCart({ edition, deviceCount: Number(state.deviceCount) || 0, carePackage: state.carePackage, careDuration: state.careDuration }),
    [edition, state]
  );

  const carePackagesList = [
    { key: "none", ...PRODUCTS.carePackages.none, Icon: Package },
    { key: "carePlus", ...PRODUCTS.carePackages.carePlus, Icon: ShieldCheck },
    { key: "carePremium", ...PRODUCTS.carePackages.carePremium, Icon: Sparkles },
  ];

  return (
    <Card data-testid="license-configurator" className="p-5 border-border bg-card">
      <div className="mb-4">
        <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-1">Lizenz-Konfigurator</div>
        <h3 className="text-lg font-semibold">Zusammenstellung für {EDITION_KEYS[edition]}</h3>
        <p className="text-sm text-muted-foreground">Wählen Sie Geräte-Anzahl und Care-Paket. Artikelnummern von Xortec werden live ergänzt.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <div>
          <Label className="text-sm mb-2 block">Anzahl Device-Lizenzen</Label>
          <Input
            data-testid="input-device-count"
            type="number"
            min={0}
            max={100000}
            value={state.deviceCount}
            onChange={(e) => setState({ deviceCount: e.target.value.replace(/[^0-9]/g, "") })}
            className="bg-secondary/40 border-border"
          />
          <p className="text-xs text-muted-foreground mt-1">Zusätzlich zur Basis-Lizenz – eine je Kamera/Kanal.</p>
        </div>
        <div>
          <Label className="text-sm mb-2 block">Laufzeit Wartung</Label>
          <select
            data-testid="select-duration"
            disabled={state.carePackage === "none"}
            value={state.careDuration}
            onChange={(e) => setState({ careDuration: e.target.value })}
            className="w-full h-10 px-3 rounded-md bg-secondary/40 border border-border text-sm disabled:opacity-40"
          >
            {PRODUCTS.durationOptions.map((d) => (
              <option key={d.key} value={d.key}>
                {d.label} – {d.years}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground mt-1">Verlängerung um 1 Monat, Preisstaffel nach Restlaufzeit.</p>
        </div>
      </div>

      <div className="mb-5">
        <Label className="text-sm mb-2 block">Care-Paket</Label>
        <RadioGroup
          value={state.carePackage}
          onValueChange={(v) => setState({ carePackage: v })}
          className="grid grid-cols-1 md:grid-cols-3 gap-3"
        >
          {carePackagesList.map(({ key, label, description, Icon }) => {
            const selected = state.carePackage === key;
            return (
              <Label
                key={key}
                htmlFor={`care-${key}`}
                data-testid={`care-${key}`}
                className={cn(
                  "flex items-start gap-3 rounded-lg border p-4 cursor-pointer tile-hover",
                  selected ? "border-primary bg-primary/10 ring-1 ring-primary/40" : "border-border bg-secondary/40 hover:bg-secondary/60"
                )}
              >
                <RadioGroupItem id={`care-${key}`} value={key} className="data-[state=checked]:border-primary data-[state=checked]:text-primary mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 font-medium text-sm">
                    <Icon className="w-4 h-4 text-primary" />
                    {label}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{description}</p>
                </div>
              </Label>
            );
          })}
        </RadioGroup>
      </div>

      {/* Cart preview */}
      <div className="rounded-lg border border-border bg-secondary/30 overflow-hidden">
        <div className="px-4 py-2 border-b border-border text-xs uppercase tracking-widest text-primary font-semibold">
          Artikel-Zusammenfassung
        </div>
        <div className="divide-y divide-border/60">
          {cart.map((it, idx) => (
            <div key={idx} className="px-4 py-3 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{it.name || it.manufacturerNo}</div>
                <div className="text-xs text-muted-foreground truncate">{it.description}</div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  <span className="font-mono">Xortec-Nr. {it.xortecNo}</span>
                  <span className="mx-2">·</span>
                  <span className="font-mono">Hersteller: {it.manufacturerNo}</span>
                  {it.url && (
                    <a href={it.url} target="_blank" rel="noreferrer" className="ml-2 text-primary hover:underline inline-flex items-center gap-1">
                      xortec.de <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs text-muted-foreground">Menge</div>
                <div className="font-bold tabular-nums">{it.qty}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground mt-3">
        Preise auf Anfrage bei der Xortec GmbH. Alle Artikel werden digital ausgeliefert.
      </p>
    </Card>
  );
}
